import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components } from '../tipos/clientes';
import type { components as Entregas } from '../tipos/entregas';
import type { components as Canales } from '../tipos/canales';
import type { components as Pagos } from '../tipos/pagos';
import { operacionesCitasCliente } from './citasCliente';
import { operacionesDocumentosCliente } from './documentosCliente';
import { operacionesPasesCliente } from './pasesCliente';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Canal = Esquema<'Channel'>;
export type PeticionCodigo = Esquema<'CodeRequest'>;
export type CodigoPedido = Esquema<'CodeRequested'>;
export type CuentaNuevaCliente = NonNullable<Esquema<'CodeConfirmation'>['new_account']>;
export type TokenCliente = Esquema<'CustomerTokenIssued'>;
export type CuentaCliente = Esquema<'CustomerAccount'>;
export type CambioCuentaCliente = Esquema<'CustomerAccountUpdate'>;
export type CambioContacto = Esquema<'ContactChangeRequest'>;
/** Idiomas de la cuenta y de «Mis datos» (`CustomerLocale`, G363). */
export type IdiomaCuenta = Esquema<'CustomerLocale'>;
export type MiPerfil = Esquema<'MyProfile'>;
export type ListaMisPerfiles = Esquema<'MyProfileList'>;
export type PermisoActual = Esquema<'ConsentCurrent'>;
export type PermisoNuevo = Esquema<'ConsentInput'>;
export type PropuestaCorreccion = Esquema<'CorrectionProposal'>;
export type ListaPropuestas = Esquema<'CorrectionProposalList'>;
export type SolicitudPrivacidad = Esquema<'PrivacyRequest'>;
export type ListaSolicitudes = Esquema<'PrivacyRequestList'>;
/** Los campos con valor por defecto en el contrato (`confirm_cancellations`, `when`) son opcionales al pedir. */
export type SolicitudNueva = Omit<Esquema<'MyPrivacyRequestCreate'>, 'confirm_cancellations' | 'when'> & Partial<Pick<Esquema<'MyPrivacyRequestCreate'>, 'confirm_cancellations' | 'when'>>;
export type Compromiso = NonNullable<Esquema<'CommitmentsProblem'>['commitments']>[number];
export type EnlaceBaja = Esquema<'UnsubscribeLinkInfo'>;
export type EnlacePermiso = Esquema<'ConsentConfirmationLinkInfo'>;
export type Identificacion = Esquema<'CustomerIdentificationRequest'>;
export type ResultadoIdentificacion = Esquema<'CustomerIdentificationResult'>;
/** Página de pago (4.9) y «Mis pagos» (4.10), de `pagos.yaml`. */
export type EnlacePago = Pagos['schemas']['PaymentLinkPublic'];
export type MiPago = Pagos['schemas']['MyPayment'];
export type ListaMisPagos = Pagos['schemas']['MyPaymentList'];
export type MiMensaje = Entregas['schemas']['MyDelivery'];
export type ListaMisMensajes = Entregas['schemas']['MyDeliveryList'];
export type MiConversacion = Canales['schemas']['MyConversation'];
export type ListaMisConversaciones = Canales['schemas']['MyConversationList'];
export type MensajeDeConversacion = Canales['schemas']['Message'];
export type ListaMensajesDeConversacion = Canales['schemas']['MessageList'];
export interface DescargaCopia {
    download_url: string;
    expires_at: string;
}

const cuenta = '/v1/customer-account';
const perfil = (id: string): `/v1/${string}` => `${cuenta}/profiles/${encodeURIComponent(id)}`;
const enlace = (base: string, token: string): `/v1/${string}` => `/v1/${base}/${encodeURIComponent(token)}`;

/**
 * Operaciones de las **páginas del cliente** (2.9 a 2.11, G157, ADR-0058), con su `operationId` y los tipos generados de
 * `clientes.yaml`. Van con el cliente de la API de la zona del cliente (`cliente/apiCliente.ts`), que solo manda el token
 * de cliente (`Authorization: Bearer`, esquema `customer`), nunca la sesión del equipo.
 *
 * - `requestCustomerCode` y `confirmCustomerCode` son públicas con la clave pública (`X-Api-Key`, sin negocio en esta
 *   fase: la provisional de las páginas de Reservaria) y no llevan el token aunque lo haya (`sinSesion`).
 * - `identifyCustomerInBusiness` lleva la clave pública **del negocio** y el token (preparada para la web pública).
 * - Los enlaces de un toque (baja, permiso, copia) son anónimos: ni token ni clave.
 */
export function operacionesClienteFinal(api: ClienteApi, clavePublica: string | null) {
    const conClave = (extra: Record<string, string> = {}, clave: string | null = clavePublica): Record<string, string> =>
        clave === null || clave === '' ? extra : { 'X-Api-Key': clave, ...extra };

    return {
        /** requestCustomerCode (alta pública: `X-Captcha-Token`, G76): la respuesta es siempre la misma (G42). */
        requestCustomerCode: (datos: PeticionCodigo, captcha: string | null, idioma?: string) =>
            api.pedir<CodigoPedido>('POST', '/v1/customer-auth/codes', {
                cuerpo: datos,
                sinSesion: true,
                cabeceras: conClave({ ...(captcha === null ? {} : { 'X-Captcha-Token': captcha }), ...(idioma === undefined ? {} : { 'Accept-Language': idioma }) }),
            }),
        /** confirmCustomerCode: `422 code_invalid` con el motivo y `attempts_remaining` (G153); `404` email sin cuenta (G154). */
        confirmCustomerCode: (verificacion: string, code: string, cuentaNueva?: CuentaNuevaCliente) =>
            api.pedir<TokenCliente>('POST', `/v1/customer-auth/codes/${encodeURIComponent(verificacion)}/confirm`, {
                cuerpo: cuentaNueva === undefined ? { code } : { code, new_account: cuentaNueva },
                sinSesion: true,
                cabeceras: conClave(),
            }),
        /** revokeCustomerToken («Salir», «No soy yo»). */
        revokeCustomerToken: () => api.pedir<undefined>('DELETE', '/v1/customer-auth/token'),
        /**
         * identifyCustomerInBusiness (token de cliente + clave pública del negocio): preparada para la web pública y los
         * widgets (fase 4); en esta fase no la usa ninguna página.
         */
        identifyCustomerInBusiness: (negocioId: string, clavePublicaNegocio: string, datos: Identificacion, clave = claveIdempotencia()) =>
            api.pedir<ResultadoIdentificacion>('POST', `${negocio(negocioId)}/customer-identifications`, {
                cuerpo: datos,
                idempotencia: clave,
                cabeceras: conClave({}, clavePublicaNegocio),
            }),

        /** getMyCustomerAccount, con su `ETag` para `updateMyCustomerAccount`. */
        getMyCustomerAccount: (): Promise<ConVersion<CuentaCliente>> => api.pedirConVersion<CuentaCliente>('GET', cuenta),
        /** updateMyCustomerAccount (If-Match): nombre e idioma (G35). */
        updateMyCustomerAccount: (cambio: CambioCuentaCliente, version: string): Promise<ConVersion<CuentaCliente>> =>
            api.pedirConVersion<CuentaCliente>('PATCH', cuenta, { cuerpo: cambio, siCoincide: version }),
        /**
         * startContactChange: código al contacto nuevo (202); el teléfono exige un código reciente (`recent_verification_required`).
         * Email vacío = quitarlo en el momento: responde 200 con la cuenta sin email, sin código (G354).
         */
        startContactChange: (datos: CambioContacto) => api.pedir<CodigoPedido | CuentaCliente | undefined>('POST', `${cuenta}/contact-changes`, { cuerpo: datos }),
        /** confirmContactChange: `409 conflict` si el teléfono es de otra cuenta (se pueden unir con esta verificación). */
        confirmContactChange: (verificacion: string, code: string) =>
            api.pedir<CuentaCliente>('POST', `${cuenta}/contact-changes/${encodeURIComponent(verificacion)}/confirm`, { cuerpo: { code } }),
        /** mergeMyCustomerAccounts: con la verificación del teléfono de la otra cuenta (T34, T35). */
        mergeMyCustomerAccounts: (verificacion: string, clave = claveIdempotencia()) =>
            api.pedir<CuentaCliente>('POST', `${cuenta}/merges`, { cuerpo: { verification_id: verificacion }, idempotencia: clave }),

        /** listMyBusinessProfiles: los negocios donde tiene perfil vinculado (G165). */
        listMyBusinessProfiles: (pagina: Pagina = {}) => api.pedir<ListaMisPerfiles>('GET', `${cuenta}/profiles${consulta({ ...pagina })}`),
        /** setMyPreferredChannel (R-04). */
        setMyPreferredChannel: (perfilId: string, channel: Canal) => api.pedir<MiPerfil>('PUT', `${perfil(perfilId)}/preferred-channel`, { cuerpo: { channel } }),
        /** recordMyConsent: un registro nuevo por canal y finalidad (G29). */
        recordMyConsent: (perfilId: string, permiso: PermisoNuevo) => api.pedir<MiPerfil>('POST', `${perfil(perfilId)}/consents`, { cuerpo: permiso }),
        /** deleteMyHealthInfo: quita las alergias o intolerancias guardadas en ese negocio (G192). */
        deleteMyHealthInfo: (perfilId: string) => api.pedir<undefined>('DELETE', `${perfil(perfilId)}/health-info`),

        /** listMyCorrectionProposals: las propuestas de un negocio (T23). */
        listMyCorrectionProposals: (estado?: PropuestaCorreccion['status'], pagina: Pagina = {}) =>
            api.pedir<ListaPropuestas>('GET', `${cuenta}/correction-proposals${consulta({ status: estado, ...pagina })}`),
        /** decideCorrectionProposal: aceptar o rechazar (caduca a los 30 días, G164). */
        decideCorrectionProposal: (propuesta: string, decision: 'accept' | 'reject') =>
            api.pedir<PropuestaCorreccion>('POST', `${cuenta}/correction-proposals/${encodeURIComponent(propuesta)}/decision`, { cuerpo: { decision } }),

        /** listMyPrivacyRequests: sus solicitudes con el nombre del negocio (G151). */
        listMyPrivacyRequests: (pagina: Pagina = {}) => api.pedir<ListaSolicitudes>('GET', `${cuenta}/privacy-requests${consulta({ ...pagina })}`),
        /**
         * createMyPrivacyRequest: copia, corrección o supresión. La supresión puede responder `401
         * recent_verification_required` y `409 commitments_pending` (G27); «Borrar cuando termine» es `when: after_commitments` (G199).
         */
        createMyPrivacyRequest: (datos: SolicitudNueva, clave = claveIdempotencia()) =>
            api.pedir<SolicitudPrivacidad>('POST', `${cuenta}/privacy-requests`, { cuerpo: datos, idempotencia: clave }),

        /** getUnsubscribeLink (anónima): negocio y canal; no escribe nada. */
        getUnsubscribeLink: (token: string) => api.pedir<EnlaceBaja>('GET', enlace('unsubscribe-links', token), { sinSesion: true }),
        /** useUnsubscribeLink (anónima): la baja con un clic (G166); no vuelve a apuntar a nadie (G156). */
        useUnsubscribeLink: (token: string) => api.pedir<undefined>('POST', enlace('unsubscribe-links', token), { sinSesion: true }),
        /** getConsentConfirmationLink (anónima): abrir el enlace nunca confirma (G155). */
        getConsentConfirmationLink: (token: string) => api.pedir<EnlacePermiso>('GET', enlace('consent-confirmations', token), { sinSesion: true }),
        /** confirmConsentByLink (anónima): «Sí, quiero recibirlas» o «No, gracias» (G162). */
        confirmConsentByLink: (token: string, decision: 'accept' | 'decline') =>
            api.pedir<undefined>('POST', enlace('consent-confirmations', token), { cuerpo: { decision }, sinSesion: true }),
        /** downloadPrivacyExport (anónima): solo al pulsar «Descargar» (G156); la dirección dura 15 minutos. */
        downloadPrivacyExport: (token: string) => api.pedir<DescargaCopia>('GET', enlace('privacy-exports', token), { sinSesion: true }),

        // ---- Pagos (4.9, 4.10) ----
        /** getPaymentLink (anónima): la página de pago; `amount` ya incluye los gastos de gestión (G292). */
        getPaymentLink: (negocioId: string, codigo: string) =>
            api.pedir<EnlacePago>('GET', `/v1/public/businesses/${encodeURIComponent(negocioId)}/payment-links/${encodeURIComponent(codigo)}`, { sinSesion: true }),
        /** listMyPayments (token de cliente): los pagos del cliente en un negocio, con sus devoluciones una a una (G293). */
        listMyPayments: (perfilId: string, pagina: Pagina = {}) => api.pedir<ListaMisPagos>('GET', `${perfil(perfilId)}/payments${consulta({ ...pagina })}`),
        /** listMyDeliveries (token de cliente, 5.7): sus mensajes de servicio en un negocio, con lo que le queda por pedir hoy. */
        listMyDeliveries: (perfilId: string, pagina: Pagina = {}) => api.pedir<ListaMisMensajes>('GET', `${perfil(perfilId)}/deliveries${consulta({ ...pagina })}`),
        /** resendMyDelivery: otra vez por su canal preferido, como mucho 3 al día por mensaje (G44; 429 si se pasa, 409 si ya no se puede). */
        resendMyDelivery: (perfilId: string, entregaId: string, clave = claveIdempotencia()) =>
            api.pedir<MiMensaje>('POST', `${perfil(perfilId)}/deliveries/${encodeURIComponent(entregaId)}/resend`, { idempotencia: clave }),

        // ---- Canales (11.10) ----
        /** listMyConversations (token de cliente): sus conversaciones con un negocio; un perfil que no es suyo, 404. */
        listMyConversations: (perfilId: string, pagina: Pagina = {}) => api.pedir<ListaMisConversaciones>('GET', `${perfil(perfilId)}/conversations${consulta({ ...pagina })}`),
        /** listMyConversationMessages: los mensajes de una (el equipo con el nombre del negocio). */
        listMyConversationMessages: (perfilId: string, conversacion: string, pagina: Pagina = {}) =>
            api.pedir<ListaMensajesDeConversacion>('GET', `${perfil(perfilId)}/conversations/${encodeURIComponent(conversacion)}/messages${consulta({ ...pagina })}`),

        // ---- Pases y Acceso (6.6, 6.7) ----
        ...operacionesPasesCliente(api),
        // ---- Documentos y Firma (9.12): la página para firmar y «Mis documentos» ----
        ...operacionesDocumentosCliente(api),
        // ---- Citas (9.9, 9.10): pedir cita, «Mis citas» y el enlace de la cita ----
        ...operacionesCitasCliente(api),
    };
}

export type OperacionesClienteFinal = ReturnType<typeof operacionesClienteFinal>;
